import { useFrame } from "@react-three/fiber";
import { Float } from "@react-three/drei";
import { useRef } from "react";
import { Check, ArrowUpRight } from "lucide-react";
import * as THREE from "three";
import { CanvasInView, Reveal, SectionTag } from "./Reveal";
import { presetProduct } from "./QuoteSection";

const METAL = { metalness: 0.95, roughness: 0.22, color: "#aebfcf" };

function Aircraft() {
  return (
    <group>
      <mesh rotation={[0, 0, Math.PI / 2]}>
        <capsuleGeometry args={[0.3, 2.4, 8, 24]} />
        <meshStandardMaterial {...METAL} />
      </mesh>
      <mesh position={[1.55, 0, 0]} rotation={[0, 0, -Math.PI / 2]}>
        <coneGeometry args={[0.3, 0.65, 24]} />
        <meshStandardMaterial {...METAL} />
      </mesh>
      <mesh position={[0.15, -0.02, 0]} rotation={[0.04, 0, 0]}>
        <boxGeometry args={[0.85, 0.05, 3.3]} />
        <meshStandardMaterial {...METAL} />
      </mesh>
      <mesh position={[-1.35, 0.32, 0]} rotation={[0, 0, -0.25]}>
        <boxGeometry args={[0.5, 0.62, 0.05]} />
        <meshStandardMaterial {...METAL} />
      </mesh>
      <mesh position={[-1.4, 0.1, 0]}>
        <boxGeometry args={[0.45, 0.04, 1.2]} />
        <meshStandardMaterial {...METAL} />
      </mesh>
      {[-0.95, 0.95].map((z) => (
        <mesh key={z} position={[0.3, -0.24, z]} rotation={[0, 0, Math.PI / 2]}>
          <cylinderGeometry args={[0.13, 0.13, 0.5, 20]} />
          <meshStandardMaterial color="#5f7185" metalness={0.9} roughness={0.3} />
        </mesh>
      ))}
      <mesh position={[0.15, -0.02, 1.67]}>
        <sphereGeometry args={[0.045, 12, 12]} />
        <meshBasicMaterial color="#00E5FF" />
      </mesh>
      <mesh position={[0.15, -0.02, -1.67]}>
        <sphereGeometry args={[0.045, 12, 12]} />
        <meshBasicMaterial color="#FFB020" />
      </mesh>
    </group>
  );
}

function AircraftScene() {
  const ring = useRef();
  useFrame((_, d) => {
    if (ring.current) ring.current.rotation.z += d * 0.15;
  });
  return (
    <>
      <ambientLight intensity={0.25} />
      <pointLight position={[5, 5, 5]} intensity={30} color="#F0C05A" />
      <pointLight position={[-5, -2, 4]} intensity={16} color="#00E5FF" />
      <spotLight position={[0, 6, 2]} angle={0.5} penumbra={0.8} intensity={40} color="#ffffff" />
      <Float speed={1.4} rotationIntensity={0.35} floatIntensity={0.9}>
        <group scale={1.15}>
          <Aircraft />
        </group>
      </Float>
      <mesh ref={ring} position={[0, -1.5, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <ringGeometry args={[2.1, 2.2, 64]} />
        <meshBasicMaterial color="#D9A441" transparent opacity={0.22} side={THREE.DoubleSide} />
      </mesh>
      <mesh position={[0, -1.52, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[2.1, 64]} />
        <meshBasicMaterial color="#00E5FF" transparent opacity={0.03} />
      </mesh>
    </>
  );
}

const CAPABILITIES = [
  "Jet A-1 procurement",
  "Supplier sourcing",
  "Price comparison",
  "Availability checks",
  "Procurement coordination",
  "Documentation",
  "Delivery coordination",
  "Multi-location requirements",
];

export default function AviationSection() {
  return (
    <section id="aviation" data-testid="aviation-section" className="relative py-24 md:py-32 overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_70%_40%,rgba(0,229,255,0.05),transparent_55%)] pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <SectionTag number="04" label="Aviation" />
          <h2 className="font-outfit text-3xl md:text-5xl font-extralight tracking-tight text-white">
            Aviation Fuel <span className="text-gold">Procurement</span>
          </h2>
          <p className="mt-6 text-white/60 text-base md:text-lg leading-relaxed max-w-lg">
            Reliable Jet A-1 sourcing for airlines and aviation operators. Ajemi coordinates
            the procurement process end to end, so your operations never wait on fuel.
          </p>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4" data-testid="aviation-capabilities">
            {CAPABILITIES.map((cap) => (
              <div key={cap} className="flex items-center gap-3">
                <span className="w-5 h-5 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center shrink-0">
                  <Check className="w-3 h-3 text-gold" />
                </span>
                <span className="text-sm text-white/70">{cap}</span>
              </div>
            ))}
          </div>
          <button
            data-testid="aviation-request-btn"
            onClick={() => presetProduct("Jet A-1")}
            className="mt-12 group inline-flex items-center gap-3 bg-gold text-ink font-outfit font-medium rounded-full pl-7 pr-2.5 py-2.5 hover:bg-goldbright hover:shadow-[0_0_40px_rgba(217,164,65,0.45)] transition-all duration-300"
          >
            Request Aviation Fuel
            <span className="bg-ink/10 rounded-full p-2 group-hover:rotate-45 transition-transform duration-300">
              <ArrowUpRight className="w-4 h-4" />
            </span>
          </button>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="relative" data-testid="aviation-3d-visual">
            <CanvasInView className="h-[380px] md:h-[520px] w-full" camera={{ position: [0.4, 1.1, 5.4], fov: 40 }} dpr={[1, 1.75]}>
              <AircraftScene />
            </CanvasInView>
            <p className="text-center text-[11px] tracking-[0.3em] uppercase text-white/30 mt-2">
              Jet A-1 · Coordinated Supply
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
